import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { deepEquals } from "../common/utils/objectUtilities";
import { dataHookFactory, deepCompareArray } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";

const applyUpdates = (state, action) => {
    for (const sitrep of ensureArray(action.payload)) {
        const current = state.data[sitrep.id]
        if (!current) {
            state.data[sitrep.id] = sitrep;
            continue;
        }
        const delta = {};
        // fields other than data element and reports are primitive to do strict equality - add to the delta if changed
        for (const field of Object.keys(sitrep).filter(field => !['data', 'elements', 'reports'].includes(field))) {
            if (current[field] === sitrep[field]) {
                continue;
            }
            delta[field] = sitrep[field];
        }
        if (!deepEquals(current.elements, sitrep.elements)) {
            delta.elements = sitrep.elements;
        }
        if (!deepEquals(current.reports, sitrep.reports)) {
            delta.reports = sitrep.reports;
        }
        const toRemove = new Set(Object.keys(current.data));
        const toUpdate = {};
        for (const [uuid, value] of Object.entries(sitrep.data)) {
            toRemove.delete(uuid);
            if (!current.data[uuid] || !deepEquals(current.data[uuid], value)) {
                toUpdate[uuid] = value;
            }
        }
        // check if we have changes to make
        if (Object.keys(toUpdate).length > 0 || toRemove.size > 0) {
            delta.data = { ...current.data, ...toUpdate };
            for (const uuid of toRemove) {
                delete delta.data[uuid];
            }
        }
        if (Object.keys(delta).length > 0) {
            state.data[sitrep.id] = { ...state.data[sitrep.id], ...delta };
        }
    }
}

const sitrepSlice = createSlice({
    name: 'sitrep',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const toRemove = new Set(Object.keys(state.data));
            for (const sitrep of ensureArray(action.payload)) {
                toRemove.delete(sitrep.id);
            }
            for (const id of toRemove) {
                delete state.data[id];
            }
            applyUpdates(state, action);
        },
        addOrUpdate: (state, action) => {
            applyUpdates(state, action);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => sitrepSlice.getInitialState());
    }
});

// #region network operations
const fetchSitreps = network => network.get('/api/v1/sitreps', sitrepSlice.actions.set);
export const createSitrep = (network, sitrep) => network.post('/api/v1/sitrep', sitrep, sitrepSlice.actions.addOrUpdate);
export const updateSitrep = (network, sitrep) => network.put('/api/v1/sitrep', sitrep, sitrepSlice.actions.addOrUpdate);
// #endregion

// #region permission checks

// #endregion

// #region selectors
const selectSitReps = state => state.sitrep.data;

const selectSitRepSummaryList = createSelector([selectSitReps], sitreps => Object.values(sitreps).map(sitrep => {
    const element = { ...sitrep };
    delete element.data;
    delete element.elements;
    delete element.reports;
    return element;
}));
const selectSitRep = id => createSelector([selectSitReps], sitreps => sitreps[id]);
const selectSitRepDef = (id, uuid) => createSelector([selectSitReps], sitreps => sitreps[id]?.elements?.find(e => e.uuid === uuid));
const selectSitRepDatum = (id, uuid) => createSelector([selectSitReps], sitreps => sitreps[id]?.data?.[uuid]);
// #endregion

// #region listeners
const listen = {
    sitrepCreated: (sitrep, { dispatch }) => dispatch(sitrepSlice.actions.addOrUpdate(sitrep)),
    sitrepUpdated: (sitrep, { dispatch }) => dispatch(sitrepSlice.actions.addOrUpdate(sitrep))
}
// #endregion
const init = { run: fetchSitreps, listen };
export const [useSitRepSummaries] = dataHookFactory(selectSitRepSummaryList, init, { comparator: deepCompareArray });
export const [useSitRep] = dataHookFactory(selectSitRep, () => init);
export const [useSitRepDef] = dataHookFactory(selectSitRepDef, () => init);
export const [useSitRepDatum] = dataHookFactory(selectSitRepDatum, () => init);

const reducerDetails = { name: sitrepSlice.name, reducer: sitrepSlice.reducer };
export default reducerDetails;